import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "preact/jsx-runtime";
import { useState, useCallback, useEffect, useRef } from 'preact/hooks';
import { LogOut, Loader2 } from 'lucide-preact';
import { googleAuthApi } from '@/api/googleAuth';
import { GoogleDriveIcon } from '@/components/common/icons/GoogleDriveIcon';
import '@googleworkspace/drive-picker-element';
// Google Workspace MIME types that are native and can't be downloaded directly
const GOOGLE_NATIVE_MIME_TYPES = [
    'application/vnd.google-apps.document',
    'application/vnd.google-apps.spreadsheet',
    'application/vnd.google-apps.presentation',
    'application/vnd.google-apps.drawing',
    'application/vnd.google-apps.form',
    'application/vnd.google-apps.site',
    'application/vnd.google-apps.script',
];
export function GoogleDrivePicker({ auth, onFilePicked, onError }) {
    const { isConnected, providerEmail, isLoading, isConnecting, connect, disconnect } = auth;
    const [isPicking, setIsPicking] = useState(false);
    const [pickedFile, setPickedFile] = useState(null);
    const [pickerAuth, setPickerAuth] = useState(null);
    const pickerRef = useRef(null);
    // Attach event listeners to the drive-picker element
    useEffect(() => {
        const el = pickerRef.current;
        if (!el)
            return;
        const handlePicked = (e) => {
            const doc = e.detail.docs[0];
            // Check if it's a Google Workspace native file
            if (GOOGLE_NATIVE_MIME_TYPES.includes(doc.mimeType)) {
                onError?.(`Google Workspace files (${doc.mimeType.split('.').pop()}) are not yet supported. Please select a regular file (PDF, image, Office document, etc.).`);
                setPickerAuth(null);
                setIsPicking(false);
                return;
            }
            const file = {
                fileId: doc.id,
                fileName: doc.name,
                mimeType: doc.mimeType,
            };
            setPickedFile(file);
            onFilePicked(file);
            setPickerAuth(null);
            setIsPicking(false);
        };
        const handleCanceled = () => {
            setPickerAuth(null);
            setIsPicking(false);
        };
        el.addEventListener('picker-picked', handlePicked);
        el.addEventListener('picker-canceled', handleCanceled);
        return () => {
            el.removeEventListener('picker-picked', handlePicked);
            el.removeEventListener('picker-canceled', handleCanceled);
        };
    }, [pickerAuth, onFilePicked, onError]);
    const openPicker = useCallback(async () => {
        setIsPicking(true);
        try {
            const tokenResponse = await googleAuthApi.getPickerToken();
            if (!tokenResponse.data) {
                onError?.('Failed to get Google Drive access. Please reconnect your account.');
                await disconnect();
                setIsPicking(false);
                return;
            }
            setPickerAuth({
                accessToken: tokenResponse.data.accessToken,
                pickerApiKey: tokenResponse.data.pickerApiKey,
            });
        }
        catch (error) {
            console.error('Failed to open Google Picker:', error);
            onError?.('Failed to open Google Drive file picker. Please try again.');
            setIsPicking(false);
        }
    }, [onError, disconnect]);
    if (isLoading) {
        return (_jsxs("div", { className: "flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 py-4", children: [_jsx(Loader2, { className: "w-4 h-4 animate-spin" }), "Checking Google Drive connection..."] }));
    }
    if (!isConnected) {
        return (_jsx("div", { className: "flex justify-center py-6", children: _jsx("button", { type: "button", onClick: () => { onError?.(undefined); connect(); }, disabled: isConnecting, className: "inline-flex items-center gap-2.5 px-4 py-2.5 rounded-lg text-sm font-medium border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 shadow-sm transition-colors disabled:opacity-50", children: isConnecting ? (_jsxs(_Fragment, { children: [_jsx(Loader2, { className: "w-4 h-4 animate-spin" }), "Connecting..."] })) : (_jsxs(_Fragment, { children: [_jsx(GoogleDriveIcon, { className: "w-5 h-5" }), "Sign in with Google Drive"] })) }) }));
    }
    return (_jsxs("div", { className: "space-y-3", children: [_jsxs("div", { className: "flex items-center justify-between text-sm", children: [_jsxs("span", { className: "text-slate-500 dark:text-slate-400", children: ["Connected as ", _jsx("span", { className: "font-medium text-slate-700 dark:text-slate-200", children: providerEmail })] }), _jsxs("button", { type: "button", onClick: disconnect, className: "inline-flex items-center gap-1 text-xs text-slate-400 hover:text-red-500 dark:hover:text-red-400 transition-colors", children: [_jsx(LogOut, { className: "w-3 h-3" }), "Disconnect"] })] }), pickerAuth && (_jsxs("drive-picker", { ref: pickerRef, "oauth-token": pickerAuth.accessToken, "developer-key": pickerAuth.pickerApiKey, title: "Select a file from Google Drive", children: [_jsx("drive-picker-docs-view", { "include-folders": "false", "select-folder-enabled": "false" }), _jsx("drive-picker-docs-view", { "enable-drives": "true" })] })), pickedFile ? (_jsxs("div", { className: "flex items-center gap-3 p-3 rounded-lg border border-primary-200 dark:border-primary-800 bg-primary-50 dark:bg-primary-900/20", children: [_jsx(GoogleDriveIcon, { className: "w-5 h-5 flex-shrink-0" }), _jsxs("div", { className: "min-w-0 flex-1", children: [_jsx("p", { className: "text-sm font-medium text-slate-900 dark:text-slate-100 truncate", children: pickedFile.fileName }), _jsx("p", { className: "text-xs text-slate-500 dark:text-slate-400", children: pickedFile.mimeType })] }), _jsx("button", { type: "button", onClick: () => {
                            setPickedFile(null);
                            openPicker();
                        }, className: "text-xs text-primary-600 dark:text-primary-400 hover:underline flex-shrink-0", children: "Change" })] })) : (_jsx("button", { type: "button", onClick: openPicker, disabled: isPicking, className: "w-full flex items-center justify-center gap-2 px-4 py-6 rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-600 hover:border-primary-400 dark:hover:border-primary-500 bg-slate-50 dark:bg-slate-700/30 text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors disabled:opacity-50", children: isPicking ? (_jsxs(_Fragment, { children: [_jsx(Loader2, { className: "w-4 h-4 animate-spin" }), "Opening Google Drive..."] })) : (_jsxs(_Fragment, { children: [_jsx(GoogleDriveIcon, { className: "w-5 h-5" }), "Pick from Google Drive"] })) }))] }));
}
